import { useState, useEffect, useContext } from 'react';
import styles from './CalculationDetailModal.module.css';
import { CalculationExpression } from './CalculationDetailData';
import DollarFormat from '../format/DollarFormat';
import CalculationVariableContainer from './CalculationVariableContainer';
import { RatioFormat, TextFormat } from '../format';
import { DataResult } from '../model/DataResult';
import Environment from '../../Environment';
import { RowDataSource } from '../model/RowDataSource';
import Alert from '../Alert/Alert';
import { AlertType } from '../Alert/AlertType';
import Spinner from '../Loading/Spinner/Spinner';
import { cachedFetch } from '../../utils/cachedFetch';
import AppContext from '../../AppContext';

interface CalculationExpressionContainerProps {
  rowType?: string;
  year: number;
  rowKey: string;
  cocode: number;
  rowDataSource?: RowDataSource;
  updateExpression: (expression: string) => void;
  sectionKey?: string;
  subSectionKey?: string;
  overridePeriodType?: string | null;
}

export default function CalculationExpressionContainer({
  rowType = '',
  year,
  rowKey,
  cocode,
  rowDataSource,
  updateExpression,
  sectionKey,
  subSectionKey,
  overridePeriodType
}: CalculationExpressionContainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [expressionData, setExpressionData] = useState<CalculationExpression>();
  const context = useContext(AppContext);
  const viewMode = context?.viewMode;
  const periodType = context?.periodType;

  // Compute effective period type at component level for both URL and label display
  const effectivePeriodType = overridePeriodType !== undefined ? overridePeriodType : periodType;

  useEffect(() => {
    if (rowDataSource) {
      const usePreviewEndpoint = viewMode === 'preview' && rowDataSource === 'CALCULATE';
      const basePath = usePreviewEndpoint ? '/preview/calculation_detail' : '/calculation_detail';
      let url = `${Environment.API_URL()}${basePath}?year=${year}&rowKey=${rowKey}&cocode=${cocode}&rowDataSource=${rowDataSource}`;

      if (rowDataSource === 'CALCULATE') {
        url += `&sectionKey=${sectionKey}`;
      }

      if (subSectionKey === 'def-redundancy-lob') {
        url += `&subSectionKey=${subSectionKey}`;
      }

      if (effectivePeriodType && effectivePeriodType.startsWith('QUARTER_')) {
        url += `&periodType=${effectivePeriodType}`;
      }

      cachedFetch(url, {
        headers: {
          Authorization: `${sessionStorage.getItem('bearerToken')}`
        }
      }).then(
        (response) => {
          if (!response.ok) {
            let error = new Error(response.statusText);
            setError(error);
          } else {
            response.json().then(
              (json: CalculationExpression) => {
                setExpressionData(json);
                updateExpression(json?.expression);
              },
              (error) => {
                console.log(error);
              }
            );
          }
        },
        (error) => {
          console.log(error);
        }
      );
    }
  }, [year, rowKey, cocode, rowDataSource, updateExpression, sectionKey, subSectionKey]);

  function displayValue(rowType: string, result: DataResult) {
    if (rowType === 'TEXT') {
      return <TextFormat textValue={result.textValue} />;
    } else if (rowType === 'RATIO') {
      return <RatioFormat showDefaultValue={true} value={result.value} />;
    } else if (rowType === 'AMOUNT') {
      return <DollarFormat showDefaultValue={true} value={result.value} />;
    } else {
      return;
    }
  }

  if (error) {
    return (
      <Alert
        alertType={AlertType.INFO}
        content={year + ': Details for this calculation are not available.'}></Alert>
    );
  } else if (!expressionData && !error) {
    return <Spinner id={`loading-${year}`}></Spinner>;
  } else if (expressionData) {
    return (
      <div className={styles.container}>
        <div className={styles.expression_container}>
          <label>{expressionData.year}{effectivePeriodType?.startsWith('QUARTER_') ? ` Q${effectivePeriodType.replace('QUARTER_', '')}` : ''} Formula</label>
          <label className="column_align_right">Result</label>
          <p data-testid={`expression-${expressionData.year}`}>{expressionData.expression}</p>
          {displayValue(rowType, expressionData.result)}
        </div>

        {expressionData.variableList.length > 0 && (
          <>
            <div className={styles.button_container}>
              <button aria-label="Expand items" onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? 'Hide elements' : 'View elements'}
              </button>
            </div>
            <CalculationVariableContainer
              isExpanded={isExpanded}
              variableList={expressionData.variableList}
            />
          </>
        )}
      </div>
    );
  }
}
